import React, {FC, useCallback, useState} from 'react'
import {usePageTitle} from '../hooks/use-page-title'
import {Layout} from "../app/layout";
import * as Yup from "yup";
import {useForm, yupResolver} from "@mantine/form";
import {Button, Container, Divider, Grid, Group, NativeSelect, Paper, SelectItem, TextInput} from "@mantine/core";
import {DatePicker} from "@mantine/dates";
import {useNavigate} from "react-router-dom";
import {AccountData} from '../pages/profile';
import {Calendar} from 'tabler-icons-react';
import moment from 'moment';
import dayjs from "dayjs";
import {useRequestWithFormDataMutation, useSpeakerMutation} from "../store/slice/customApiSlice";

type SignInPayload = {
  name: string;
  surname: string;
  patronymic: string;
  accountAddress: string
  advocacyFormId?: string
  advocacyForm?: ReferenceType
  denomination?: string
  email?: string
  lawyerOfficeEstablishmentNotificationDate?: Date | string
  lawyersInformation?: string
  phoneNumber?: string
  specialization?: string
  stateRegistrationInformation?: string
}

declare type RefType = {
  code: string;
  id: number;
  title: string;
  editable: boolean;
};

export type ReferenceType = {
  id: number;
  title: string;
  type: RefType;
};

type Props = {
  advocacyForm?: AccountData;
  advocacyList?: SelectItem[];
}

const schema = Yup.object().shape({
  accountAddress: Yup.string().required('Введите адрес'),
});

export const ProfileForm: FC<Props> = ({advocacyForm, advocacyList}) => {
  const [advocacyFormId, setAdvocacyFormId] = useState<string>();

  usePageTitle('Профиль')
  const [isSubmit, setIsSubmit] = useState(false);
  const history = useNavigate()

  const form = useForm({
    validate: yupResolver(schema),
    initialValues: {
      name: advocacyForm?.userData.name || '',
      surname: advocacyForm?.userData.surname || '',
      patronymic: advocacyForm?.userData.patronymic || '',
      accountAddress: advocacyForm?.lawyerPersonalAccountData.accountAddress || '',
      advocacyFormId: advocacyForm?.lawyerPersonalAccountData.advocacyForm.id.toLocaleString() || '',
      advocacyForm: advocacyForm?.lawyerPersonalAccountData.advocacyForm || {
        id: advocacyForm?.lawyerPersonalAccountData.advocacyForm.id || 0,
        title: advocacyForm?.lawyerPersonalAccountData.advocacyForm.title || '',
        type: {
          code: advocacyForm?.lawyerPersonalAccountData.advocacyForm.type.code || '',
          editable: advocacyForm?.lawyerPersonalAccountData.advocacyForm.type.editable || false,
          id: advocacyForm?.lawyerPersonalAccountData.advocacyForm.type.id || 0,
          title: advocacyForm?.lawyerPersonalAccountData.advocacyForm.type.title || '',
        }
      },
      denomination: advocacyForm?.lawyerPersonalAccountData.denomination || '',
      email: advocacyForm?.lawyerPersonalAccountData.email || '',
      lawyerOfficeEstablishmentNotificationDate: advocacyForm?.lawyerPersonalAccountData.lawyerOfficeEstablishmentNotificationDate || '',
      lawyersInformation: advocacyForm?.lawyerPersonalAccountData.lawyersInformation || '',
      phoneNumber: advocacyForm?.lawyerPersonalAccountData.phoneNumber || '',
      specialization: advocacyForm?.lawyerPersonalAccountData.specialization || '',
      stateRegistrationInformation: advocacyForm?.lawyerPersonalAccountData.stateRegistrationInformation || '',
    },
  });

  const [request] = useRequestWithFormDataMutation();

  const edit = useCallback(async (values: SignInPayload) => {
    setIsSubmit(true)
    const dateValue = values.lawyerOfficeEstablishmentNotificationDate ? moment(values.lawyerOfficeEstablishmentNotificationDate).format('YYYY-MM-DD') : '';

    try {
      await request({
        path: `/lawyers/personal-account`,
        method: "PUT",
        body: {
          name: values.name,
          surname: values.surname,
          patronymic: values.patronymic,
          accountAddress: values.accountAddress,
          advocacyFormId: values.advocacyFormId,
          denomination: values.denomination,
          email: values.email,
          lawyerOfficeEstablishmentNotificationDate: dateValue,
          lawyersInformation: values.lawyersInformation,
          phoneNumber: values.phoneNumber,
          specialization: values.specialization,
          stateRegistrationInformation: values.stateRegistrationInformation,
          attachments: null
        },
      })
      setIsSubmit(false)
      history(`/profile`)
    } catch (e) {
      setIsSubmit(false)
    }
  }, [history])

  const renderRedirect = () => {
    return history('/profile')
  }

  return (
    <Layout>
      <Container size={720} sx={{
        marginTop: '5rem'
      }}>

        <Paper radius="md" p="md" withBorder>
          <Divider label="Редактирование данных" labelPosition="center" my="xl"/>
          <form onSubmit={form.onSubmit((values) => edit(values))}>
            <Group position={'apart'}>
              <Grid>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    {...form.getInputProps('name')}
                    required
                    label="Имя"
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    required
                    label="Фамилия"
                    {...form.getInputProps('surname')}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    required={false}
                    label="Отчество"
                    {...form.getInputProps('patronymic')}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    label="Специализация"
                    {...form.getInputProps('specialization')}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <NativeSelect
                    {...form.getInputProps('advocacyFormId')}
                    label="Форма адвокатской деятельности"
                    data={advocacyList ? advocacyList : []}
                    onChange={(e) => {
                      form.setFieldValue('advocacyFormId', `${e.target.value}`);
                      setAdvocacyFormId(e.target.value)
                    }}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    label="Наименование адвокатского кабинета"
                    {...form.getInputProps('denomination')}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    label="Юридический адрес"
                    {...form.getInputProps('accountAddress')}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    label="Номер телефона"
                    {...form.getInputProps('phoneNumber')}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <TextInput
                    label="Адрес электронной почты"
                    {...form.getInputProps('email')}
                  />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <DatePicker
                    locale='ru'
                    inputFormat="YYYY-MM-DD"
                    icon={<Calendar size={16}/>}
                    onChange={
                      (date) => {
                        form.setFieldValue('lawyerOfficeEstablishmentNotificationDate', date ? dayjs(date).format('YYYY-MM-DD') : '')
                      }
                    }
                    {...form.getInputProps('lawyerOfficeEstablishmentNotificationDate')}
                    value={form?.values?.lawyerOfficeEstablishmentNotificationDate ? new Date(form.values.lawyerOfficeEstablishmentNotificationDate ?? '') : null}
                    label="Дата уведомления об учреждении адвокатского кабинета"
                  />
                </Grid.Col>
                {
                  advocacyFormId === "22" || advocacyFormId === "23" ? (
                    <>
                      <Grid.Col xs={12} md={6}>
                        <TextInput
                          label="Сведения об адвокатах, осуществляющих деятельность в составе организации"
                          {...form.getInputProps('lawyersInformation')}
                        />
                      </Grid.Col>
                      <Grid.Col xs={12} md={6}>
                        <TextInput
                          label="Сведения о государственной регистрации адвокатской организации"
                          {...form.getInputProps('stateRegistrationInformation')}
                        />
                      </Grid.Col>
                    </>
                  ) : null
                }
              </Grid>
            </Group>

            <Group position="center" mt="xl">
              <Button onClick={renderRedirect} color='gray'>Отменить</Button>
              <Button disabled={isSubmit} type="submit" variant='filled' color={'teal'}>Сохранить</Button>
            </Group>
          </form>
        </Paper>
      </Container>
    </Layout>
  )
}
